import { useState } from 'preact/hooks';
import type { WidgetConfig } from '@digital-cs/shared';
import type { SupportedLocale } from '../../types';
import { resolveFaqContent } from '../../i18n/widget-content';

/**
 * 常见问题(FAQ)accordion —— 点击 question 就地展开/收起预设 answer,不发 WS/不走 AI。
 * 从 WebChatPanel 抽出,web 客服与数字人客服共用(复用 dcs-web-faq-* 样式)。
 * answer 直接来自后台配置,按当前 locale 取译文(resolveFaqContent);
 * 非中文 locale 无译文的条目整条隐藏,避免中英混杂(后端补译后自动恢复)。
 */
export function FaqList({
  faqs,
  locale,
  t,
}: {
  faqs: WidgetConfig['faqs'];
  locale: SupportedLocale;
  t: (key: string) => string;
}) {
  const [expandedFaqId, setExpandedFaqId] = useState<string | null>(null);
  const visible = faqs
    .map((f) => ({ f, content: resolveFaqContent(f, locale) }))
    .filter((x): x is { f: WidgetConfig['faqs'][number]; content: { question: string; answer: string } } => x.content !== null);
  if (!visible.length) return null;
  return (
    <div class="dcs-web-faq" role="list" aria-label={t('web.faqTitle')}>
      <div class="dcs-web-faq-title">{t('web.faqTitle')}</div>
      {visible.map(({ f, content }) => {
        const { question, answer } = content;
        const isOpen = expandedFaqId === f.id;
        return (
          <div
            key={f.id}
            class={`dcs-web-faq-item${isOpen ? ' is-open' : ''}`}
            role="listitem"
          >
            <button
              type="button"
              class="dcs-web-faq-q"
              aria-expanded={isOpen}
              onClick={() => setExpandedFaqId((prev) => (prev === f.id ? null : f.id))}
            >
              <span class="dcs-web-faq-q-text">{question}</span>
              <span class="dcs-web-faq-chevron" aria-hidden="true">
                {isOpen ? '−' : '+'}
              </span>
            </button>
            {isOpen && <div class="dcs-web-faq-a">{answer}</div>}
          </div>
        );
      })}
    </div>
  );
}
